/**
 * The global filter criteria a saved report was built from.
 *
 * The report persists `definitionExpression` — the compiled where-clause — and that is what
 * the query runs on. But the expression can't be turned back into criteria, since compiling
 * it folds in dataset scoping, user identity and quoting. So the criteria are snapshotted
 * beside it, and the filter form hydrates from those.
 */

import { initialState as filterInitialState } from '../../../redux/slices/filterSlice';

/** Filter-slice fields that are not part of a report's criteria. */
const NON_CRITERIA_FILTER_KEYS: ReadonlySet<string> = new Set([
    // In-flight request flags, meaningless once the query settles.
    'isQuerying',
    'isEstimatedTreatmentQuerying',
    'isEstimatedActivityQuerying',
    'isActualTreatmentQuerying',
    'isActualActivityQuerying',
    'isPostFireProjectQuerying',

    // The compiled output of the criteria. The report carries `definitionExpression` as its
    // own field and that stays the source of truth, so hydration must not race it.
    'definitionExpression',
    'filterExpression',

    // Which filter UI is driving.
    'isBasicFilterActive',
    'isCustomFilterActive',

    // Spatial selection: never reached the expression, and an ArcGIS geometry doesn't
    // survive a JSON round trip.
    'activeDrawTool',
    'filterGeometry',
    'filterFeatureSelection',

    // Derived counts, recalculated on hydrate rather than trusted from the blob.
    'activeFilterCount',
    'customFilterCount',

    // Mirrored in from other slices. Viewport and environment, not filters.
    'currentMapExtent',
    'currentMapScale',
    'currentMapZoom',
    'currentMapResolution',
    'rootServiceUrl',
    'nifcToken',
    'config',
]);

/** The criteria fields, derived once from the slice's own initial state. */
const CRITERIA_KEYS: readonly string[] = Object.keys(filterInitialState).filter(
    (key) => !NON_CRITERIA_FILTER_KEYS.has(key)
);

/**
 * A report's filter criteria.
 *
 * Loosely typed on purpose — the filter slice is untyped JS with around eighty fields.
 */
export interface SummaryReportFilterState {
    criteria: Record<string, unknown>;
}

/** Root state, narrowed to the slice this module reads. */
interface PartialFilterState {
    filter?: Record<string, unknown>;
}

/**
 * The current criteria, for saving with a report.
 *
 * Read imperatively at save time rather than selected, so the view doesn't re-render on
 * every keystroke in the filter form.
 */
export function captureFilterState(state: PartialFilterState): SummaryReportFilterState {
    const filter = state.filter ?? {};
    const criteria: Record<string, unknown> = {};

    CRITERIA_KEYS.forEach((key) => {
        criteria[key] = filter[key];
    });

    return { criteria };
}

/**
 * The criteria to apply for a snapshot, as a complete set of assignments.
 *
 * Every key is returned, not just the ones the snapshot carries — a key it predates resolves
 * to its initial value. That makes loading report B replace report A's criteria rather than
 * union with them.
 */
export function resolveFilterCriteria(snapshot: SummaryReportFilterState): Record<string, unknown> {
    const saved = snapshot?.criteria ?? {};
    const defaults = filterInitialState as Record<string, unknown>;
    const resolved: Record<string, unknown> = {};

    CRITERIA_KEYS.forEach((key) => {
        resolved[key] = key in saved ? saved[key] : defaults[key];
    });

    return resolved;
}
